import { format, formatDate } from 'date-fns'
import { id } from 'date-fns/locale'

const toIdr = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 0,
}).format

const toIdrFraction = new Intl.NumberFormat('id-ID', {
  style: 'currency',
  currency: 'IDR',
  maximumFractionDigits: 10,
  minimumFractionDigits: 0,
}).format

const toIdrWithoutSymbol = new Intl.NumberFormat('id-ID', {
  useGrouping: true,
}).format

function NaNFallback(value: number | string, fallback: number): number {
  value = Number(value)
  return Number.isNaN(value) ? fallback : value
}

function dateId(
  date: Date | string | number,
  opts = {
    formatStr: 'dd MMMM yyyy',
  }
): string {
  return format(date, opts.formatStr, { locale: id })
}

function dateTimeStamp(date: Date | string | number): string {
  return format(date, 'dd-MM-yyyy, HH:mm')
}

function toDigit(value: string): number {
  const parse = value.replace(/[^0-9]/g, '')
  return Number(parse)
}

function toNumber(value: string): number {
  const floatRegex = /^-?[0-9]*[.,]?[0-9]*$/
  return floatRegex.test(value) ? Number(value) : 0
}

function formatToIDRMillions(value: number): string {
  // Define thresholds
  const billionThreshold = 1_000_000_000
  const millionThreshold = 1_000_000

  // Check if the value is in billions
  if (value >= billionThreshold) {
    // Format value in billions
    const valueInBillions = value / billionThreshold
    return `${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(valueInBillions)} Billion`
  }

  // Check if the value is in millions
  if (value >= millionThreshold) {
    // Format value in millions
    const valueInMillions = value / millionThreshold
    return `${new Intl.NumberFormat('id-ID', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(valueInMillions)} Juta`
  }

  // Default formatting for values less than one million
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)
}

function formatAccountingNumber(num: number, isCurrency = false) {
  const value = isCurrency ? toIdr(num) : toIdrWithoutSymbol(num)
  const isNegative = num < 0
  return isNegative ? `(${value})` : `${value}`
}

const SI_SYMBOL = ['', 'k', 'M', 'G', 'T', 'P', 'E']

/**
 * @see https://stackoverflow.com/a/40724354
 */
function abbreviateNumber(num: number) {
  // what tier? (determines SI symbol)
  const tier = (Math.log10(Math.abs(num)) / 3) | 0

  // if zero, we don't need a suffix
  if (tier == 0) return num

  // get suffix and determine scale
  const suffix = SI_SYMBOL[tier]
  const scale = Math.pow(10, tier * 3)

  // scale the number
  const scaled = num / scale

  // format number and add suffix
  return scaled.toFixed(1) + suffix
}

function toDateSimple(date: string | Date) {
  return formatDate(date, 'yyyy-MM-dd')
}

/* --------------------------------- EXPORT --------------------------------- */
export {
  toIdr,
  toIdrFraction,
  toIdrWithoutSymbol,
  formatAccountingNumber,
  NaNFallback,
  dateId,
  toDigit,
  toNumber,
  formatToIDRMillions,
  abbreviateNumber,
  toDateSimple,
  dateTimeStamp,
}
